import { addVisit, getVisitById, upsertVisits } from '../store/idbHandler';
import { VisitsEntity, Patient as PatientDirPt } from '../api/fetchPatientDirByDocOid';
import { ZodSchema } from 'zod';
import {
    advicesEntitySchema,
    createReferalDataSchema,
    currentMedicationsSchema,
    dentalProcedureSchema,
    diagnosisEntityOrSymptomsEntitySchema,
    drugAllergySchema,
    examinationEntitySchema,
    familyHistorySchema,
    followupSchema,
    foodOthersAllergySchema,
    iMilestoneHistorySchema,
    injectionsEntitySchema,
    iVaccineStatusSchema,
    labTestsEntityOrProceduresEntitySchema,
    labVitalEntitySchema,
    lifestyleHabitsSchema,
    medicationsEntitySchema,
    notesSchema,
    opIopElementSchema,
    opSpecElementSchema,
    opVisionElementSchema,
    otherMedicalHistorySchema,
    pastProceduresSchema,
    patientMedicalConditionSchema,
    procedureSchema,
    recentTravelHistorySchema,
    vitalsEntitySchema,
} from '../validators/prescription-types-bundle.zod';

export const updateAddVisit = async (p: PatientDirPt, v: VisitsEntity): Promise<void> => {
    const localVisit = await getVisitById(v.vid);

    localVisit
        ? await upsertVisits([
              {
                  ...localVisit,
                  patient: {
                      ...localVisit.patient,
                      id: p.id,
                      localId: p.id,
                  },
                  prescriptionId: v.pxid,
                  isCompleted: 1,
                  prescriptionUrl: v.url,
                  name: v.name,
                  status: v.status,
                  type: v.type,
                  doc_name: v.doc_name,
              },
          ])
        : await addVisit({
              id: v.vid,
              docid: v.docid,
              patient: {
                  localId: p.id,
                  id: p.id,
                  phone: p.profile.personal.phone,
                  name: {
                      f: p.profile.personal.name,
                  },
              },
              clinicId: v.clinicid,
              createdAt: new Date(v.visit_date),
              prescriptionId: v.pxid,
              isCompleted: 1,
              prescriptionUrl: v.url,
              name: v.name,
              status: v.status,
              type: v.type,
              doc_name: v.doc_name,
          });
};

export const sanitizeItem = <I>(arr: I[] | undefined, Schema: ZodSchema<I>, type: string): I[] => {
    if (!Array.isArray(arr)) return [];
    return arr
        .map((v) => {
            const result = Schema.safeParse(v);
            if (!result.success) {
                console.log(`${type} sanitizeItem item`, v);
                console.log(`${type} sanitizeItem result`, result);
            }
            return result.success ? result.data : null;
        })
        .filter((v): v is I => v !== null);
};

export const sanitizeProp = <I>(
    item: I | undefined,
    Schema: ZodSchema<I>,
    type: string,
): I | undefined => {
    if (!item) return item;
    const result = Schema.safeParse(item);
    if (!result.success) {
        console.log(`${type} sanitizeItem item`, item);
        console.log(`${type} sanitizeItem result`, result);
    }
    return result.data;
};

export const sanitizeRxTool = (tool: any) => {
    return {
        ...tool,
        diagnosis: sanitizeItem(tool.diagnosis, diagnosisEntityOrSymptomsEntitySchema, 'diagnosis'),
        symptoms: sanitizeItem(tool.symptoms, diagnosisEntityOrSymptomsEntitySchema, 'symptoms'),
        medications: sanitizeItem(tool.medications, medicationsEntitySchema, 'medications'),
        injections: sanitizeItem(tool.injections, injectionsEntitySchema, 'injections'),
        labTests: sanitizeItem(tool.labTests, labTestsEntityOrProceduresEntitySchema, 'labTests'),
        labVitals: sanitizeItem(tool.labVitals, labVitalEntitySchema, 'labVitals'),
        followup: sanitizeProp(tool.followup, followupSchema, 'followup'),
        advices: sanitizeItem(tool.advices, advicesEntitySchema, 'advices'),
        prescriptionNotes: sanitizeProp(tool.prescriptionNotes, notesSchema, 'prescriptionNotes'),
        privateNotes: sanitizeProp(tool.privateNotes, notesSchema, 'privateNotes'),
        medicalHistory: {
            vitals: sanitizeItem(tool?.medicalHistory?.vitals, vitalsEntitySchema, 'vitals'),
            patientHistory: {
                patientMedicalConditions: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.patientMedicalConditions,
                    patientMedicalConditionSchema,
                    'patientHistory.patientMedicalConditions',
                ),
                familyHistory: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.familyHistory,
                    familyHistorySchema,
                    'patientHistory.familyHistory',
                ),

                lifestyleHabits: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.lifestyleHabits,
                    lifestyleHabitsSchema,
                    'patientHistory.lifestyleHabits',
                ),

                recentTravelHistory: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.recentTravelHistory,
                    recentTravelHistorySchema,
                    'patientHistory.recentTravelHistory',
                ),

                currentMedications: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.currentMedications,
                    currentMedicationsSchema,
                    'patientHistory.currentMedications',
                ),

                drugAllergy: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.drugAllergy,
                    drugAllergySchema,
                    'patientHistory.drugAllergy',
                ),

                foodOtherAllergy: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.foodOtherAllergy,
                    foodOthersAllergySchema,
                    'patientHistory.foodOtherAllergy',
                ),

                pastProcedures: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.pastProcedures,
                    pastProceduresSchema,
                    'patientHistory.pastProcedures',
                ),

                otherMedicalHistory: sanitizeItem(
                    tool.medicalHistory?.patientHistory?.otherMedicalHistory,
                    otherMedicalHistorySchema,
                    'patientHistory.otherMedicalHistory',
                ),
            },
            examinations: sanitizeItem(
                tool.medicalHistory?.examinations,
                examinationEntitySchema,
                'examinations',
            ),
        },
        refer: sanitizeProp(tool.refer, createReferalDataSchema, 'refer'),
        milestones: sanitizeItem(tool.milestones, iMilestoneHistorySchema, 'milestones'),
        administeredVaccines: sanitizeItem(
            tool.administeredVaccines,
            iVaccineStatusSchema,
            'administeredVaccines',
        ),

        allMilestones: sanitizeItem(tool.allMilestones, iMilestoneHistorySchema, 'allMilestones'),

        vaccinationHistory: sanitizeItem(
            tool.vaccinationHistory,
            iVaccineStatusSchema,
            'vaccinationHistory',
        ),

        dentalProcedures: sanitizeItem(
            tool.dentalProcedures,
            dentalProcedureSchema,
            'dentalProcedures',
        ),
        opVision: sanitizeItem(tool.opVision, opVisionElementSchema, 'opVision'),
        opIop: sanitizeItem(tool.opIop, opIopElementSchema, 'opIop'),
        opCurrentSpec: sanitizeItem(tool.opCurrentSpec, opSpecElementSchema, 'opCurrentSpec'),
        opSubjectiveRefraction: sanitizeItem(
            tool.opSubjectiveRefraction,
            opSpecElementSchema,
            'opSubjectiveRefraction',
        ),

        opAutoRefraction: sanitizeItem(
            tool.opAutoRefraction,
            opSpecElementSchema,
            'opAutoRefraction',
        ),

        opFinalPrescription: sanitizeItem(
            tool.opFinalPrescription,
            opSpecElementSchema,
            'opFinalPrescription',
        ),

        procedures: sanitizeItem(tool.procedures, procedureSchema, 'procedures'),
    };
};
